import moment from 'moment-timezone'
import useAppDispatch from '../../../hooks/useAppDispatch'
import useEventTimezone from '../../../hooks/useEventTimezone'
import { BibFieldError, getDirtyBibSelector, getDirtyTagSelector, getSelectedEntrySelector, getSelectedEntryTeamSelector, PanelState, setBibFieldError, setDirtyInput, setEnterKeyPress, setPanelState, setSelectedEntry, setTagFieldError, TagFieldError } from '../checkinSlice'
import useAppSelector from '../../../hooks/useAppSelector'
import removeLeadingZeroes from '../../../utils/removeLeadingZeroes'
import { useCallback, useMemo } from 'react'
import { isRelaySharedBibTeam } from '../../../components/common/Bib'
import { addUnsyncedRequests, getS3EntriesSelector } from '../../data/dataSlice'
import { useEntries } from './useEntries'
import useCheckinSettings from './useCheckinSettings'
import { S3CheckinEntry } from '../../../app/types'
import { setSnackbarMessages } from '../../snackbar/snackbarSlice'

const useCheckInEntry = () => {
  const dispatch = useAppDispatch()
  const eventTimezone = useEventTimezone()
  const entriesFromApi = useAppSelector(getS3EntriesSelector)
  const entries = useEntries(entriesFromApi)
  const { checkinAllSettings } = useCheckinSettings()
  const askForTag = checkinAllSettings.settings?.askForTag
  const selectedEntry = useAppSelector(getSelectedEntrySelector)
  const dirtyBib = removeLeadingZeroes(useAppSelector(getDirtyBibSelector) || '')
  const dirtyTag = useAppSelector(getDirtyTagSelector) || ''
  const team = useAppSelector(getSelectedEntryTeamSelector)
  const isRelaySharedBib = team && isRelaySharedBibTeam(team)
  const selectedEntryWithTeam = useMemo(() => {
    return selectedEntry && team ? [selectedEntry, team] : []
  }, [selectedEntry, team])
  // if event hasn't yet synced their event settings, we need to make sure that '' is considered default
  // and it has same effect as NONE
  const chipDisambiguationPolicyMemo = useMemo(() => {
    return ['', 'NONE']
  }, [])

  return useCallback(() => {
    // must need an entry selected
    if (!selectedEntry) {
      dispatch(setPanelState(PanelState.ERROR))
      return
    }

    // if chipDisambiguationPolicy is set to NONE, Bib must be unique across the event
    // if chipDisambiguationPolicy is set to BY_TIME or BY_MAT, Bib must be unique across the race
    const isDisambiguation = !chipDisambiguationPolicyMemo.includes(checkinAllSettings.event.chipDisambiguationPolicy ?? '')

    const filteredEntries = entries?.filter(entry => entry.entry_id !== selectedEntry.entryId)

    let hasError = false

    // cannot assign Bib 0
    if (dirtyBib === '0') {
      dispatch(setBibFieldError(BibFieldError.ZERO))
      hasError = true
    } else {
      const findBibMatch = (entry: S3CheckinEntry) => {
        const isBibMatch = entry.entry_bib === dirtyBib
        return isDisambiguation ? isBibMatch && entry.race_id === selectedEntry.raceId : isBibMatch
      }

      const findDifferentTeamBibMatch = (entry: S3CheckinEntry) => {
        const isDifferentTeamBibMatch = entry.entry_bib === dirtyBib && entry.team_id !== selectedEntry.teamId
        return isDisambiguation ? isDifferentTeamBibMatch && entry.race_id === selectedEntry.raceId : isDifferentTeamBibMatch
      }

      // make sure bib is unique, across the whole event or, if relay, across other teams
      const isBibDuplicate = !isRelaySharedBib
        ? dirtyBib && filteredEntries.find(findBibMatch)
        : dirtyBib && filteredEntries.find(findDifferentTeamBibMatch)

      if (isBibDuplicate) {
        dispatch(setBibFieldError(BibFieldError.DUPLICATE))
        hasError = true
      } else {
        dispatch(setBibFieldError(BibFieldError.NONE))
      }
    }

    // tag validation is independent of bib validation so both can surface at once.
    // when askForTag is false, the backend still assigns the bib as the chip/tag
    // (see ctlive-api's updateEntryCheckinData.ts bib->chip fallback), so we must
    // check dirtyBib against other entries' tags in that case instead of dirtyTag
    const tagCompareValue = askForTag ? dirtyTag : dirtyBib

    const findTagMatch = (entry: S3CheckinEntry) => {
      const isTagMatch = entry.entry_tag === tagCompareValue
      return isDisambiguation ? isTagMatch && entry.race_id === selectedEntry.raceId : isTagMatch
    }

    const findDifferentTeamTagMatch = (entry: S3CheckinEntry) => {
      const isDifferentTeamTagMatch = entry.entry_tag === tagCompareValue && entry.team_id !== selectedEntry.teamId
      return isDisambiguation ? isDifferentTeamTagMatch && entry.race_id === selectedEntry.raceId : isDifferentTeamTagMatch
    }

    // make sure tag is unique, across the whole event or, if relay, across other teams
    const isTagDuplicate = !isRelaySharedBib
      ? tagCompareValue && filteredEntries.find(findTagMatch)
      : tagCompareValue && filteredEntries.find(findDifferentTeamTagMatch)

    if (isTagDuplicate) {
      dispatch(setTagFieldError(TagFieldError.DUPLICATE))
      hasError = true
    } else {
      dispatch(setTagFieldError(TagFieldError.NONE))
    }

    if (hasError) {
      return
    }

    const lastModifiedTime = moment.tz(eventTimezone!).unix()

    // update relay team bibs accordingly
    if (isRelaySharedBib && dirtyBib && selectedEntryWithTeam?.length) {
      selectedEntryWithTeam.forEach(entry => {
        const isCurrentEntrySelected = selectedEntry.entryId === entry.entryId
        dispatch(addUnsyncedRequests([
          {
            regChoiceName: entry.regChoiceName,
            raceId: entry.raceId,
            entryId: entry.entryId,
            teamId: entry.teamId,
            teamType: entry.teamType,
            teamShareBib: entry.teamShareBib,
            lastModifiedTime,
            bib: !isCurrentEntrySelected ? dirtyBib : '',
            tag: askForTag ? (!isCurrentEntrySelected ? dirtyTag : '') : undefined,
            checkedIn: isCurrentEntrySelected ? true : entry.checkedIn,
            checkInTime: isCurrentEntrySelected ? lastModifiedTime : entry.checkInTime,
            chipDisambiguationPolicy: checkinAllSettings.event.chipDisambiguationPolicy ?? 'NONE',
          }
        ]))
      })
    } else {
      dispatch(addUnsyncedRequests([
        {
          regChoiceName: selectedEntry.regChoiceName,
          raceId: selectedEntry.raceId,
          entryId: selectedEntry.entryId,
          teamId: selectedEntry.teamId,
          teamType: selectedEntry.teamType,
          teamShareBib: selectedEntry.teamShareBib,
          checkedIn: true,
          lastModifiedTime,
          checkInTime: lastModifiedTime,
          bib: dirtyBib,
          tag: askForTag ? dirtyTag : undefined,
          chipDisambiguationPolicy: checkinAllSettings.event.chipDisambiguationPolicy ?? 'NONE',
        }
      ]))
    }

    dispatch(setSelectedEntry(undefined))
    dispatch(setPanelState(PanelState.HIDDEN))
    dispatch(setDirtyInput(false))
    dispatch(setEnterKeyPress(false))
    dispatch(setSnackbarMessages({
      messageLines: [
        {
          message: dirtyBib
            ? `${selectedEntry.fullName} has been checked-in with bib number ${dirtyBib}`
            : `${selectedEntry.fullName} has been checked-in without a bib number`
        }
      ],
      severity: 'success'
    }))
  }, [
    dirtyBib,
    dirtyTag,
    askForTag,
    eventTimezone,
    dispatch,
    entries,
    selectedEntry,
    isRelaySharedBib,
    selectedEntryWithTeam,
    checkinAllSettings.event.chipDisambiguationPolicy,
    chipDisambiguationPolicyMemo
  ])
}

export default useCheckInEntry
